"use client";

/**
 * Free "Is this reportable?" RIDDOR checker (/free/riddor-reportable-checker,
 * SEO plan Workstream D row 10 and Workstream E row 5).
 *
 * Runs entirely in the browser: the answers live in React state only.
 * Nothing is fetched, stored or put in the URL, and the only analytics are
 * the fixed values in ./analytics.ts. The questions and answers are a private
 * region (masked from session capture). The logic and every word of the
 * outcome are lib/marketing/riddor-checker/checker.ts, pinned path by path in
 * __tests__/lib/marketing/riddor-checker/checker.test.ts.
 */

import { useId, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  ADVICE_FOOTER,
  BLOCKS,
  NOT_LEGAL_ADVICE,
  SCOPE_LINE,
  VERDICT_HEADLINES,
  assess,
  pruneAnswers,
  type RiddorAnswers,
} from "@/lib/marketing/riddor-checker/checker";
import { PRIVATE_REGION_CLASS, SECONDARY_BUTTON_CLASS } from "@/components/marketing/exposure-calculators/fields";
import { RiddorBlock } from "./block-view";
import { trackRiddorChecker } from "./analytics";

const VERDICT_CLASS = {
  reportable: "border-danger-line bg-danger-bg text-danger",
  record: "border-attn-line bg-attn-bg text-attn",
  "not-reportable": "border-gray-200 bg-gray-50 text-gray-900",
  advice: "border-attn-line bg-attn-bg text-attn",
} as const;

export function RiddorChecker() {
  const posthog = usePostHog();
  const baseId = useId();
  const usedReported = useRef(false);
  const completedReported = useRef(false);
  const [answers, setAnswers] = useState<RiddorAnswers>({});

  const { questions, result } = assess(answers);

  function answer(id: keyof RiddorAnswers, value: string) {
    if (!usedReported.current) {
      usedReported.current = true;
      trackRiddorChecker(posthog, { event: "riddor_checker_used" });
    }
    // Changing an earlier answer drops the follow-ups that no longer apply.
    const next = pruneAnswers({ ...answers, [id]: value } as RiddorAnswers);
    setAnswers(next);
    if (!completedReported.current && assess(next).result) {
      completedReported.current = true;
      trackRiddorChecker(posthog, { event: "riddor_checker_completed" });
    }
  }

  return (
    <section
      aria-labelledby={`${baseId}-heading`}
      data-testid="riddor-checker"
      data-clarity-mask="true"
      className={`${PRIVATE_REGION_CLASS} rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6`}
    >
      <h2 id={`${baseId}-heading`} className="text-2xl font-bold text-gray-900">
        Is it reportable?
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">
        Answer the questions about what happened. Your answers stay in your browser. Nothing you choose is sent to us.
      </p>

      <div className="mt-5 space-y-5 rounded-xl border border-gray-200 bg-white p-4">
        {questions.map((question) => {
          const hintId = question.hint ? `${baseId}-${question.id}-hint` : undefined;
          return (
            <fieldset key={question.id} data-testid={`riddor-question-${question.id}`} aria-describedby={hintId}>
              <legend className="text-sm font-semibold text-gray-900">{question.label}</legend>
              {question.hint ? (
                <p id={hintId} className="mt-1 text-sm text-gray-600">
                  {question.hint}
                </p>
              ) : null}
              {question.list ? (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
                  {question.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-2 space-y-2">
                {question.options.map((option) => (
                  <label key={option.value} className="flex items-start gap-3 text-sm text-gray-800">
                    <input
                      type="radio"
                      name={`${baseId}-${question.id}`}
                      value={option.value}
                      checked={answers[question.id] === option.value}
                      onChange={() => answer(question.id, option.value)}
                      className="mt-0.5 h-5 w-5 shrink-0 border-gray-300 text-accent focus:ring-accent"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>

      <div className="mt-4">
        <button type="button" onClick={() => setAnswers({})} className={SECONDARY_BUTTON_CLASS}>
          Start again
        </button>
      </div>

      <div aria-live="polite" data-testid="riddor-result" className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
        {result === null ? (
          <p className="text-sm text-gray-600">Answer every question to see whether it is reportable.</p>
        ) : (
          <>
            <p
              data-testid="riddor-verdict"
              data-verdict={result.verdict}
              className={`rounded-lg border px-4 py-3 text-xl font-extrabold ${VERDICT_CLASS[result.verdict]}`}
            >
              {VERDICT_HEADLINES[result.verdict]}
            </p>
            <div className="mt-4 space-y-5">
              {result.blocks.map((id) => (
                <RiddorBlock key={id} block={BLOCKS[id]} />
              ))}
            </div>
            {result.verdict === "advice" ? (
              <p data-testid="riddor-advice-footer" className="mt-5 text-sm leading-relaxed text-gray-700">
                {ADVICE_FOOTER}
              </p>
            ) : null}
            <p data-testid="riddor-scope" className="mt-5 text-sm leading-relaxed text-gray-700">
              {SCOPE_LINE} {NOT_LEGAL_ADVICE}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
